import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { marketingApi } from '../../api/marketing.js';
import { Spinner, ErrorBox, EmptyBox, Field } from '../../components/ui.js';
import { localDate, datetime, num } from '../../lib/format.js';

/**
 * 회원탈퇴목록 — 원본 ../xadmin/marketing/outmember_email.php.
 *
 * 탈퇴한 회원 중 **그 이메일로 아직 재가입하지 않은** 사람만 일자·이메일로
 * 보여준다(outmember.service.ts 의 listOutmemberEmails 참고). 마케팅이
 * 탈퇴자에게 다시 연락할 때, 이미 돌아온 사람에게 또 보내지 않으려는 목록이다.
 *
 * 원본과 다르게 한 것: 원본은 검은 배경의 자기만의 표였다 - 이 메뉴의 다른
 * 화면들(이메일다운·도메인 모니터링)과 같은 디자인으로 맞췄다(2026-10-01 확인).
 * 형식이 이상한 이메일을 빨갛게 보여주는 것(원본의 filter_var 자리)은 그대로
 * 옮겼다 - .td-danger 가 원본의 style="color:red" 와 같은 자리다.
 */

/** 기본 기간 - 원본과 같다(어제 ~ 오늘). 서버 기본값과 같은 계산이어야 한다
 * (outmember.service.ts 의 defaultOutmemberRange) - 안 그러면 입력칸이 빈 채로
 * 보이면서 서버는 조용히 다른 기간으로 조회한다. */
function defaultRange(): { from: string; to: string } {
  return { from: localDate(new Date(Date.now() - 86_400_000)), to: localDate() };
}

export function OutmemberEmailPage() {
  const initial = defaultRange();
  const [draftFrom, setDraftFrom] = useState(initial.from);
  const [draftTo, setDraftTo] = useState(initial.to);
  const [range, setRange] = useState(initial);

  const listing = useQuery({
    queryKey: ['marketing', 'outmember-emails', range],
    queryFn: () => marketingApi.outmemberEmails(range),
  });

  const rows = listing.data?.rows ?? [];

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">회원탈퇴목록</h1>
          <p className="page-desc">
            탈퇴한 회원 중 그 이메일로 아직 재가입하지 않은 사람만 모아 보여줍니다.
            형식이 이상한 이메일은 빨간 글자로 표시합니다.
          </p>
        </div>
      </header>

      <form
        className="lookup-form"
        onSubmit={(event) => {
          event.preventDefault();
          setRange({ from: draftFrom, to: draftTo });
        }}
      >
        <div className="lookup-range">
          <Field label="일자검색 시작">
            <input
              className="input"
              type="date"
              value={draftFrom}
              onChange={(event) => setDraftFrom(event.target.value)}
            />
          </Field>
          <Field label="일자검색 끝">
            <input
              className="input"
              type="date"
              value={draftTo}
              onChange={(event) => setDraftTo(event.target.value)}
            />
          </Field>
        </div>

        <div className="lookup-actions">
          <span className="lookup-count">
            탈퇴일(wdate) 기준 - 가입·탈퇴가 여러 번이어도 재가입이 남아 있으면 뺍니다.
          </span>
          <button className="btn" type="submit" disabled={listing.isFetching}>
            {listing.isFetching ? '검색 중…' : '검색'}
          </button>
        </div>
      </form>

      {listing.isPending ? (
        <Spinner />
      ) : listing.isError ? (
        <ErrorBox error={listing.error} onRetry={() => void listing.refetch()} />
      ) : (
        <>
          <div className="summary">
            <div className="summary__item">
              <span className="summary__label">검색된 총 수</span>
              <strong className="summary__value">{num(listing.data.total)}건</strong>
            </div>
          </div>

          {rows.length === 0 ? (
            <EmptyBox>이 기간에 탈퇴한 회원이 없습니다.</EmptyBox>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>일자</th>
                    <th>이메일</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.wdate}-${i}`}>
                      <td className="td-muted">{datetime(r.wdate)}</td>
                      <td className={r.emailValid ? undefined : 'td-danger'}>{r.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
